import type { OcrManualReviewItem } from '../api/adminOps';
import type { ChipTone } from '../components/ui';

/**
 * DM-5809: the queue mixes two holds whose Approve has different effects, so
 * every row and the decision drawer say which kind it is and what each
 * button does.
 */
export interface HoldPresentation {
  readonly label: string;
  readonly tone: ChipTone;
  readonly description: string;
  readonly approveLabel: string;
  readonly approveEffect: string;
  readonly rejectEffect: string;
}

const OCR_QUARANTINE: HoldPresentation = {
  label: 'OCR quarantine',
  tone: 'warn',
  description: 'Classification was ambiguous before OCR ran.',
  approveLabel: 'Approve & re-run OCR',
  approveEffect: 'Approve re-runs OCR on this document.',
  rejectEffect: 'Reject marks the document failed and closes its slot on the donation.',
};

const POST_OCR_HOLD: HoldPresentation = {
  label: 'Post-OCR hold',
  tone: 'crit',
  description:
    'OCR finished, but the document was held: a fraud or image-integrity signal, sensitive data that needs redaction, or a bank statement that did not match the donation. The specific reason is not recorded on the document.',
  approveLabel: 'Accept as evidence',
  approveEffect:
    'Approve accepts the document as final evidence despite the flag. OCR does not re-run.',
  rejectEffect:
    'Reject marks the document failed and reopens the requirement it fulfilled, so the donor must supply new evidence.',
};

export const holdPresentation = (
  item: Pick<OcrManualReviewItem, 'processing_status'>
): HoldPresentation =>
  item.processing_status === 'requires_review' ? POST_OCR_HOLD : OCR_QUARANTINE;

/** When the document entered its current hold; an older backend sends only the upload time. */
export const queuedAt = (item: Pick<OcrManualReviewItem, 'queued_at' | 'created_at'>): string =>
  item.queued_at ?? item.created_at;
